import React from 'react';
import { LabeledInput, LabeledTextarea } from './Fields';

interface Props {
  workflowKey: string;
  description: string;
  isActive: boolean;
  baseline?: {
    workflowKey: string;
    version: number;
    isActive: boolean;
    description: string;
    definition: unknown;
  } | null;
  diffActive?: boolean;
  hasDefinitionChanges?: boolean;
  onChangeWorkflowKey: (value: string) => void;
  onChangeDescription: (value: string) => void;
  onChangeIsActive: (value: boolean) => void;
}

export default function CatalogPublishCard({
  workflowKey,
  description,
  isActive,
  baseline,
  diffActive = false,
  hasDefinitionChanges = false,
  onChangeWorkflowKey,
  onChangeDescription,
  onChangeIsActive,
}: Props) {
  const normalizeText = (value: unknown) => String(value ?? '').trim();

  const diffRowClass = (changed: boolean) => {
    if (!diffActive) return '';
    if (!baseline) return '';
    if (!changed) return 'border-l-4 border-transparent';
    return 'border-l-4 border-indigo-600 bg-indigo-950/20 text-indigo-200';
  };

  const changedWorkflowKey = baseline ? normalizeText(workflowKey) !== normalizeText(baseline.workflowKey) : false;
  const changedDescription = baseline ? normalizeText(description) !== normalizeText(baseline.description) : false;
  const changedActive = baseline ? Boolean(isActive) !== Boolean(baseline.isActive) : false;

  return (
    <div className="pac-glass rounded-md p-3 space-y-3">
      <h4 className="text-sm font-semibold text-neutral-200">Workflow</h4>

      {diffActive && baseline && (
        <div className={
          `text-xs rounded px-2 py-2 border ` +
          (hasDefinitionChanges
            ? 'border-indigo-600/40 bg-indigo-950/30 text-indigo-200'
            : 'border-neutral-800 pac-glass text-neutral-400')
        }
        >
          {hasDefinitionChanges ? 'Definition changed (publishing will create a new version).' : 'No definition changes detected vs selected workflow.'}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className={`rounded ${diffRowClass(changedWorkflowKey)}`}>
          <LabeledInput label="Workflow key *" value={workflowKey} placeholder="audio_processing_v1" onChange={onChangeWorkflowKey} />
        </div>
      </div>

      <div className={`rounded ${diffRowClass(changedDescription)}`}>
        <LabeledTextarea
          label="Description"
          value={description}
          placeholder="One sentence about what this workflow does."
          onChange={onChangeDescription}
          rows={3}
          description="Stored inside workflow definition settings (metadata)."
        />
      </div>

      <div className={`rounded ${diffRowClass(changedActive)} px-2 py-1`}>
        <label className="flex items-center gap-2 text-sm text-neutral-200">
          <input type="checkbox" className="accent-emerald-500" checked={isActive} onChange={(e) => onChangeIsActive(e.target.checked)} />
          Active
        </label>
      </div>
    </div>
  );
}
